import React from 'react';

import dayjs from '~/timeInit';

import CheckIcon from '@mui/icons-material/Check';
import { Box, ButtonBase, Tooltip } from '@mui/material';

import { SupplierTimeline, TimelineRange, isInRange, percentOf, realSaleDate } from './timeline';
import { collectionToSelect, formatAmountWithAud, formatDate, shownCollectionId } from './utils';

export const TRACK_HEIGHT = 56;
const MARKER_SIZE = 12;
export const SHOWN_MARKER_SIZE = 20;

interface Props {
  supplier: SupplierTimeline;
  range: TimelineRange;
  tickValues: Array<number>;
  now: number;
  selectedCollectionId?: string;
  onSelectCollection?: (collectionId?: string) => void;
}

export default function CompetitorOfferTimelineTrack({
  supplier: { property, history },
  range,
  tickValues,
  now,
  selectedCollectionId,
  onSelectCollection,
}: Props) {
  const shownId = shownCollectionId(history, selectedCollectionId);
  const pct = (time: number) => `${percentOf(time, range)}%`;
  const saleStart = realSaleDate(property.saleStart, now);
  const saleEnd = realSaleDate(property.saleEnd, now);

  const bookableFrom = property.firstAvailableNight ? dayjs(property.firstAvailableNight).valueOf() : undefined;
  const bookableTo = property.lastAvailableNight
    ? dayjs(property.lastAvailableNight).endOf('day').valueOf()
    : undefined;
  const bookableBand =
    bookableFrom !== undefined && bookableTo !== undefined && bookableFrom < range.to && bookableTo > range.from
      ? { left: Math.max(bookableFrom, range.from), right: Math.min(bookableTo, range.to) }
      : undefined;

  const collections = history
    .filter((entry) => entry.collectionStartedAt)
    .map((entry) => ({ ...entry, time: dayjs(entry.collectionStartedAt).valueOf() }))
    .filter((entry) => isInRange(entry.time, range));

  return (
    <Box position="relative" height={TRACK_HEIGHT}>
      {tickValues.map((tick) => (
        <Box
          key={tick}
          position="absolute"
          top={0}
          bottom={0}
          borderLeft={1}
          borderColor="divider"
          sx={{ left: pct(tick) }}
        />
      ))}
      {bookableBand && (
        <Tooltip
          title={`Bookable nights ${formatDate(property.firstAvailableNight)} – ${formatDate(property.lastAvailableNight)}`}
        >
          <Box
            position="absolute"
            top={8}
            bottom={8}
            bgcolor="action.selected"
            borderRadius={1}
            sx={{
              left: pct(bookableBand.left),
              width: `${percentOf(bookableBand.right, range) - percentOf(bookableBand.left, range)}%`,
            }}
          />
        </Tooltip>
      )}
      {saleStart !== undefined && isInRange(saleStart, range) && (
        <Tooltip title={`Sale starts ${formatDate(property.saleStart)}`}>
          <Box
            position="absolute"
            top={0}
            bottom={0}
            borderLeft={2}
            borderColor="primary.main"
            sx={{ left: pct(saleStart), borderLeftStyle: 'dashed' }}
          />
        </Tooltip>
      )}
      {saleEnd !== undefined && isInRange(saleEnd, range) && (
        <Tooltip title={`Sale ends ${formatDate(property.saleEnd)}`}>
          <Box
            position="absolute"
            top={0}
            bottom={0}
            borderLeft={2}
            borderColor="primary.main"
            sx={{ left: pct(saleEnd) }}
          />
        </Tooltip>
      )}
      {collections.map((entry) => {
        const shown = entry.collectionId === shownId;
        const size = shown ? SHOWN_MARKER_SIZE : MARKER_SIZE;
        const selectable = entry.seen && !!onSelectCollection;
        const markerSx = {
          position: 'absolute',
          width: size,
          height: size,
          borderRadius: '50%',
          border: 2,
          borderColor: entry.seen ? 'success.main' : 'text.disabled',
          bgcolor: entry.seen ? 'success.main' : 'background.paper',
          color: 'success.contrastText',
          left: pct(entry.time),
          top: (TRACK_HEIGHT - size) / 2,
          transform: 'translateX(-50%)',
          zIndex: shown ? 1 : undefined,
          boxShadow: shown ? 3 : undefined,
        } as const;
        return (
          <Tooltip
            key={entry.collectionId}
            title={
              entry.seen
                ? `Found ${formatDate(entry.collectionStartedAt)} · ${formatAmountWithAud(entry.leadPrice, entry.leadCurrency, entry.leadPriceAud)}${shown ? ' · On show' : ' · Click to show'}`
                : `Not found ${formatDate(entry.collectionStartedAt)}`
            }
          >
            {selectable ? (
              <ButtonBase
                aria-label={
                  shown
                    ? `Collection of ${formatDate(entry.collectionStartedAt)}, on show`
                    : `Show the collection of ${formatDate(entry.collectionStartedAt)}`
                }
                onClick={() =>
                  onSelectCollection(collectionToSelect(entry.collectionId, history, selectedCollectionId))
                }
                sx={markerSx}
              >
                {shown && <CheckIcon sx={{ fontSize: 14 }} />}
              </ButtonBase>
            ) : (
              // Not a disabled button: browsers send no mouse events to one, which would hide the tooltip.
              <Box sx={markerSx}>{shown && <CheckIcon sx={{ fontSize: 14 }} />}</Box>
            )}
          </Tooltip>
        );
      })}
      {isInRange(now, range) && (
        <Tooltip title={`Today ${formatDate(dayjs(now).toISOString())}`}>
          <Box position="absolute" top={0} bottom={0} borderLeft={2} borderColor="error.main" sx={{ left: pct(now) }} />
        </Tooltip>
      )}
    </Box>
  );
}
